// © 2026 Buddy Media LLC. All rights reserved.
import { useEffect, useState } from "react";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { ApiError, apiFetch } from "@/lib/api";

// /admin/operations/{frontend,backend,wire-test}-worker — per-worker
// server-resident admin surface. Same component shape, parameterized
// by worker id.
//
// One commit, multiple leaves, single component implementation per
// `one-commit-per-leaf-default` exception (the ProviderServersPage
// pattern). Frontend exports three named pages; nav-config + router.tsx
// wire them up to the matching leaf keys. T-337 renamed frontend-worker/backend-worker to
// frontend-worker / backend-worker (BW_WORKER_NAME flip) and added
// wire-test-worker on wire-test-worker.

type Health = "ok" | "stale" | "unknown";

type WorkerTaskCounts = {
  queued: number;
  in_flight: number;
  complete: number;
  failed: number;
};

type RecentEvent = {
  ts: string;
  event_type: string;
  message: string;
};

type WorkerHealth = {
  worker: string;
  label: string;
  host: string;
  health: Health;
  last_heartbeat_at: string | null;
  task_counts: WorkerTaskCounts;
  recent_warns_24h: number;
  recent_errors_24h: number;
  recent_events: RecentEvent[];
};

type WorkerHealthResponse = {
  workers: WorkerHealth[];
  generated_at: string;
};

const HEALTH_BAND: Record<Health, string> = {
  ok: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300",
  stale: "border-amber-500/40 bg-amber-500/15 text-amber-300",
  unknown: "border-slate-500/40 bg-slate-500/15 text-slate-300",
};

const STATIC_PROFILES: Record<string, {
  bootstrap: string;
  account: string;
  ssh_status: string;
  notes: string;
}> = {
  "frontend-worker": {
    bootstrap: "T-034 (May 2026); renamed from frontend-worker in T-337",
    account: "marc@rajsent.com (secondary Max x20)",
    ssh_status:
      "git SSH wired; deploy-host SSH (10.10.8.7 → buddywatch-dev) wired post-paste-perms fix; cluster mesh authorized 2026-05-03.",
    notes:
      "Polling daemon `buddywatch-orchestration-poll.service` runs on this host (frontend-worker); claims tasks assigned to `frontend-worker` (legacy `frontend-worker` still accepted per T-336 KNOWN_WORKERS) and spawns claude --print per task. Self-heals 401s by re-minting via /api/internal/worker-bearer/frontend-worker/mint.",
  },
  "backend-worker": {
    bootstrap: "T-032 / T-038 (May 2026, post-T-033 migration); renamed from backend-worker in T-337",
    account: "Marc's primary Max x20",
    ssh_status:
      "MCP read+write green, git SSH wired, deploy-host SSH wired, headless claude responsive. Cluster mesh authorized 2026-05-03 + 2026-05-05 (10.10.8.4 SSH inbound + outbound).",
    notes:
      "Polling daemon `buddywatch-orchestration-poll.service` runs on this host (backend-worker); same flow as frontend-worker. SSH outbound to 10.10.8.4 (MCP host) authorized 2026-05-05.",
  },
  "wire-test-worker": {
    bootstrap: "T-337 (May 2026) — added alongside the frontend-worker/backend-worker rename",
    account: "Separate Claude account per workers-distinct-claude-accounts (employee-driven)",
    ssh_status:
      "Cluster mesh + MCP green on 10.10.8.9 (wire-test-worker). KNOWN_WORKERS entry added in T-336.",
    notes:
      "Wire-test worker hosting employee-driven test runs against MCP. Distinct from frontend/backend workers in lifecycle (not a polling-daemon production worker on the chat-Opus dispatch path). Anthropic account is intentionally separate per workers-distinct-claude-accounts to avoid quota cross-contamination.",
  },
};

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.data as { detail?: string } | undefined)?.detail;
    return `${fallback} (HTTP ${err.status})${detail ? `: ${detail}` : ""}`;
  }
  return fallback;
}

function formatAge(iso: string | null): string {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  const seconds = Math.floor((Date.now() - dt.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h ago`;
}

function WorkerPage({ workerId }: { workerId: string }) {
  const [data, setData] = useState<WorkerHealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    apiFetch<WorkerHealthResponse>("/api/admin/observability/worker-health")
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(describeError(err, "Load failed"));
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const w = data?.workers.find((x) => x.worker === workerId) ?? null;
  const profile = STATIC_PROFILES[workerId];

  return (
    <AdminPageShell
      actions={
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:border-slate-500"
        >
          Refresh
        </button>
      }
    >
      <div className="space-y-6">
        <AdminCard className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="text-base font-semibold text-white">
              {w?.label ?? workerId}
            </h2>
            {w && (
              <span
                className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${HEALTH_BAND[w.health]}`}
              >
                {w.health.toUpperCase()}
              </span>
            )}
          </div>
          {profile && (
            <p className="font-mono text-xs text-slate-500">
              {workerId} — {w?.host ?? "host pending probe"}
            </p>
          )}
        </AdminCard>

        {error && (
          <AdminCard className="border-rose-500/40 bg-rose-500/10">
            <p className="text-sm text-rose-200">{error}</p>
          </AdminCard>
        )}

        {profile && (
          <AdminCard className="space-y-3">
            <h3 className="text-sm font-semibold text-white">
              Worker profile
            </h3>
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              <Row label="Bootstrap" value={profile.bootstrap} />
              <Row label="Anthropic account" value={profile.account} />
              <Row label="SSH status" value={profile.ssh_status} span2 />
              <Row label="Notes" value={profile.notes} span2 />
            </dl>
          </AdminCard>
        )}

        {w ? (
          <AdminCard className="space-y-4">
            <h3 className="text-sm font-semibold text-white">
              Live status
            </h3>
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              <Row
                label="Last heartbeat"
                value={formatAge(w.last_heartbeat_at)}
              />
              <Row
                label="Warn / error (24h)"
                value={`${w.recent_warns_24h} / ${w.recent_errors_24h}`}
              />
              <Row
                label="Task counts"
                value={`Q:${w.task_counts.queued} • IF:${w.task_counts.in_flight} • C:${w.task_counts.complete} • F:${w.task_counts.failed}`}
                mono
                span2
              />
            </dl>
            <p className="text-[11px] text-slate-500">
              Live data sourced from{" "}
              <code>/api/admin/observability/worker-health</code>; for
              cluster-wide view see{" "}
              <code>/admin/dashboard/cluster-topology</code>.
            </p>
          </AdminCard>
        ) : data && !error ? (
          <AdminCard>
            <p className="text-sm text-slate-400">
              Worker <code>{workerId}</code> not found in current
              health snapshot. Backend known workers may have rotated.
            </p>
          </AdminCard>
        ) : !error ? (
          <AdminCard>
            <p className="text-sm text-slate-400">Loading…</p>
          </AdminCard>
        ) : null}
      </div>
    </AdminPageShell>
  );
}

export function OperationsFrontendWorkerPage() {
  return <WorkerPage workerId="frontend-worker" />;
}

export function OperationsBackendWorkerPage() {
  return <WorkerPage workerId="backend-worker" />;
}

export function OperationsWireTestWorkerPage() {
  return <WorkerPage workerId="wire-test-worker" />;
}

function Row({
  label,
  value,
  mono = false,
  span2 = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  span2?: boolean;
}) {
  return (
    <div className={span2 ? "sm:col-span-2" : undefined}>
      <dt className="text-[11px] uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd
        className={`mt-1 break-words text-sm text-slate-200 ${mono ? "font-mono" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}
